import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import type { ReactElement } from 'react';
import React from 'react';
import type { Column, TableCellProps, TableHeaderProps, TableRowProps } from 'react-table';
import { useFilters, useRowSelect, useSortBy, useTable } from 'react-table';

import { Loader, NoData, useIsMobileDevice } from '@swf/poker';

import { TXTMOB_No_results } from './Table.messages.json';
import styles from './Table.scss';
import { renderSelectGame, renderSelectable } from './TableAddons';
import TableSlider from './TableSlider';
import TopBar from './TopBar';

export interface Props<T extends object> {
  columns: Column<T>[];
  data: T[];
  isGame?: boolean;
  selectable?: boolean;
  selectGame?: boolean;
  sortable?: boolean;
  searchBy?: keyof T;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actionButtons?: React.ComponentType<any>;
  loading?: boolean;
  enableMobileSlider?: boolean;
  rowClassName?: (row: T) => string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowComponent?: React.ComponentType<any>;
  type?: 'approved' | 'blocked' | 'pending';
}

function Table<T extends object>({
  columns,
  loading,
  data,
  selectable,
  selectGame,
  sortable,
  actionButtons,
  enableMobileSlider = false,
  rowClassName,
  rowComponent: RowComponent,
  type = 'approved'
}: Props<T>): ReactElement {
  const isMobileDevice = useIsMobileDevice();
  const instance = useTable<T>({ columns, data }, useFilters, useSortBy, useRowSelect, (hooks) => {
    selectable && renderSelectable(hooks);
    selectGame && renderSelectGame(hooks);
  });
  const { getTableProps, getTableBodyProps, headers, rows, prepareRow } = instance;
  const enableTableSlider = enableMobileSlider && isMobileDevice && rows.length && !loading;

  return (
    <>
      {actionButtons && <TopBar instance={instance} actionButtons={actionButtons} type={type} />}
      <TableSlider enable={!!enableTableSlider}>
        <table
          {...getTableProps()}
          className={classNames(styles.table, {
            [styles.selectable]: selectable,
            [styles.slide]: enableTableSlider
          })}
        >
          <thead className={styles.tableHead}>
            <tr>
              {headers.map((column, index) => {
                const headerProps: TableHeaderProps = column.getHeaderProps(
                  sortable ? column.getSortByToggleProps() : undefined
                );

                if (column.id === 'selection' && !isMobileDevice) return null;
                if (column.id === 'role' && isMobileDevice) return null;

                return (
                  <th
                    {...headerProps}
                    key={column.id}
                    colSpan={selectable && index === 1 && !isMobileDevice ? 2 : 1}
                  >
                    {column.render('Header')}
                  </th>
                );
              })}
            </tr>
          </thead>
          {!loading && (
            <tbody
              className={classNames(styles.tableBody, {
                [styles.viewOnly]: !selectable
              })}
              {...getTableBodyProps()}
            >
              {rows.map((row, i) => {
                prepareRow(row);
                const rowProps: TableRowProps = row.getRowProps();

                return RowComponent ? (
                  <RowComponent row={row} rowProps={rowProps} key={rowProps.key} />
                ) : (
                  <tr {...rowProps} className={rowClassName && rowClassName(row.original)}>
                    {row.cells.map((cell) => {
                      const cellProps: TableCellProps = cell.getCellProps();
                      if (cell.column.id === 'userId' && isMobileDevice) {
                        const statusCell = row.cells.find((cell) => cell.column.id === 'role');
                        return (
                          <td {...cellProps} key={cell.column.id}>
                            <span className={styles.userName}>{cell.render('Cell')}</span> <br />
                            {statusCell && statusCell.render('Cell')}
                          </td>
                        );
                      }

                      if (cell.column.id === 'role' && isMobileDevice) return null;

                      return (
                        <td {...cellProps} key={cellProps.key}>
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </TableSlider>
      {loading && <Loader loading={loading} className={styles.loader} />}
      {!rows.length && !loading && (
        <NoData
          className={styles.noData}
          primaryText={<Translation {...TXTMOB_No_results} />}
          secondaryText=""
        />
      )}
      {isMobileDevice && actionButtons
        ? React.createElement(actionButtons, { tableInstance: instance })
        : null}
    </>
  );
}

export default Table;
